import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserPlan } from "@/hooks/useUserPlan";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { FunnelDialog } from "@/components/FunnelDialog";
import { ProfileMenu } from "@/components/ProfileMenu";
import { 
  Magnet, 
  Plus, 
  FileText, 
  Users, 
  BarChart3, 
  ExternalLink,
  MoreVertical,
  Trash2,
  Eye,
  Edit,
  Copy,
  LayoutTemplate,
  TrendingDown,
  FileSpreadsheet,
  Crown,
  Shield,
  Settings
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Form {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  _count?: {
    sessions: number;
    completed: number;
  };
}

// Component for managing subscription
function ManageSubscriptionButton() {
  const navigate = useNavigate();

  return (
    <Button 
      variant="outline" 
      size="sm" 
      onClick={() => navigate("/manage-subscription")}
      className="flex-shrink-0"
    >
      <Settings className="mr-2 h-4 w-4" />
      Gerenciar Assinatura
    </Button>
  );
}

export default function Dashboard() {
  const { user, loading: authLoading, signOut, isAuthenticated, session } = useAuth();
  const { role, monthlyUsage, canCreateForm, canExportCsv, isLoading: planLoading, checkSubscription, subscriptionEnd } = useUserPlan();
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteFormId, setDeleteFormId] = useState<string | null>(null);
  const [funnelForm, setFunnelForm] = useState<{ id: string; title: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const purchaseHandled = useRef(false);

  // Check for purchase success (subscription or loose leads) - process only once
  useEffect(() => {
    if (purchaseHandled.current) return;
    const isUpgrade = searchParams.get("upgrade") === "success";
    const isLeads = searchParams.get("leads_purchase") === "success";
    if (!isUpgrade && !isLeads) return;

    purchaseHandled.current = true;
    // Clear params immediately so a remount/refresh can't re-trigger
    setSearchParams({}, { replace: true });

    toast({
      title: isUpgrade ? "🎉 Bem-vindo ao Plano Pro!" : "🎉 Leads adicionados!",
      description: isUpgrade
        ? "Seu upgrade foi realizado com sucesso. Aproveite os recursos ilimitados!"
        : "Sua compra de leads avulsos foi confirmada com sucesso.",
    });

    // Send confirmation emails (admin + customer)
    supabase.functions
      .invoke("notify-payment", { body: { type: isUpgrade ? "subscription" : "leads" } })
      .catch((e) => console.error("notify-payment failed:", e));

    // Sincroniza plano e credita leads avulsos automaticamente
    checkSubscription();
  }, [searchParams, setSearchParams, toast, checkSubscription]);



  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/auth");
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchForms();
    }
  }, [isAuthenticated]);

  const fetchForms = async () => {
    try {
      const { data, error } = await supabase
        .from("forms")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch session counts for each form
      const formsWithCounts = await Promise.all(
        (data || []).map(async (form) => {
          const { count: totalSessions } = await supabase
            .from("form_sessions")
            .select("*", { count: "exact", head: true })
            .eq("form_id", form.id);

          const { count: completedSessions } = await supabase
            .from("form_sessions")
            .select("*", { count: "exact", head: true })
            .eq("form_id", form.id)
            .eq("status", "completed");

          return {
            ...form,
            _count: {
              sessions: totalSessions || 0,
              completed: completedSessions || 0,
            },
          };
        })
      );

      setForms(formsWithCounts);
    } catch (error) {
      console.error("Error fetching forms:", error);
      toast({
        title: "Erro ao carregar formulários",
        description: "Não foi possível carregar seus formulários.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteFormId) return;

    try {
      const { error } = await supabase
        .from("forms")
        .delete()
        .eq("id", deleteFormId);

      if (error) throw error;

      setForms(forms.filter((f) => f.id !== deleteFormId));
      toast({
        title: "Formulário excluído",
        description: "O formulário foi excluído com sucesso.",
      });
    } catch (error) {
      console.error("Error deleting form:", error);
      toast({
        title: "Erro ao excluir",
        description: "Não foi possível excluir o formulário.",
        variant: "destructive",
      });
    } finally {
      setDeleteFormId(null);
    }
  };

  const handleDuplicate = async (formId: string) => {
    try {
      // Fetch the original form
      const { data: originalForm, error: formError } = await supabase
        .from("forms")
        .select("*")
        .eq("id", formId)
        .single();

      if (formError) throw formError;

      // Generate new slug
      const newSlug = `${originalForm.slug}-copia-${Date.now().toString(36)}`;

      // Create duplicated form
      const { data: newForm, error: createError } = await supabase
        .from("forms")
        .insert({
          title: `${originalForm.title} (Cópia)`,
          slug: newSlug,
          description: originalForm.description,
          is_active: false,
          logo_url: originalForm.logo_url,
          color_primary: originalForm.color_primary,
          color_secondary: originalForm.color_secondary,
          color_accent: originalForm.color_accent,
          color_text_light: originalForm.color_text_light,
          color_text_dark: originalForm.color_text_dark,
          ai_question_count: originalForm.ai_question_count,
          ai_example_questions: originalForm.ai_example_questions,
          ai_final_prompt: originalForm.ai_final_prompt,
          thank_you_message: originalForm.thank_you_message,
          use_lead_history: originalForm.use_lead_history,
          user_id: user?.id,
        })
        .select()
        .single();

      if (createError) throw createError;

      // Fetch and duplicate form fields
      const { data: originalFields, error: fieldsError } = await supabase
        .from("form_fields")
        .select("*")
        .eq("form_id", formId)
        .order("sort_order");

      if (fieldsError) throw fieldsError;

      if (originalFields && originalFields.length > 0) {
        const newFields = originalFields.map((field) => ({
          form_id: newForm.id,
          field_type: field.field_type,
          label: field.label,
          placeholder: field.placeholder,
          options: field.options,
          is_required: field.is_required,
          sort_order: field.sort_order,
        }));

        const { error: insertFieldsError } = await supabase
          .from("form_fields")
          .insert(newFields);

        if (insertFieldsError) throw insertFieldsError;
      }

      toast({
        title: "Formulário duplicado",
        description: "O formulário foi duplicado com sucesso.",
      });

      // Refresh the forms list
      fetchForms();
    } catch (error) {
      console.error("Error duplicating form:", error);
      toast({
        title: "Erro ao duplicar",
        description: "Não foi possível duplicar o formulário.",
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleExportCSV = async (formId: string, formTitle: string) => {
    try {
      // Fetch all sessions for this form
      const { data: sessions, error: sessionsError } = await supabase
        .from("form_sessions")
        .select("id, lead_email, status, started_at, completed_at, pdf_url")
        .eq("form_id", formId)
        .order("started_at", { ascending: false });

      if (sessionsError) throw sessionsError;

      if (!sessions || sessions.length === 0) {
        toast({
          title: "Nenhum dado",
          description: "Este formulário ainda não tem respostas.",
        });
        return;
      }

      const sessionIds = sessions.map((s) => s.id);

      // Fetch all responses for these sessions
      const { data: responses, error: responsesError } = await supabase
        .from("form_responses")
        .select("session_id, question, answer, question_order")
        .in("session_id", sessionIds)
        .order("question_order");

      if (responsesError) throw responsesError;

      // Get all unique questions in order
      const questionsMap = new Map<number, string>();
      responses?.forEach((r) => {
        if (!questionsMap.has(r.question_order)) {
          questionsMap.set(r.question_order, r.question);
        }
      });
      const sortedQuestions = Array.from(questionsMap.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([_, q]) => q);

      // Build CSV headers
      const headers = ["Email", "Status", "Data Início", "Data Conclusão", "PDF Gerado", ...sortedQuestions];

      // Build rows
      const rows = sessions.map((session) => {
        const sessionResponses = responses?.filter((r) => r.session_id === session.id) || [];
        
        // Create a map of question_order to answer for this session
        const answersMap = new Map<number, string>();
        sessionResponses.forEach((r) => {
          answersMap.set(r.question_order, r.answer);
        });

        // Build row with answers in correct order
        const answerColumns = Array.from(questionsMap.keys())
          .sort((a, b) => a - b)
          .map((order) => answersMap.get(order) || "");

        return [
          session.lead_email,
          session.status === "completed" ? "Completo" : session.status === "in_progress" ? "Em andamento" : "Abandonado",
          new Date(session.started_at).toLocaleString("pt-BR"),
          session.completed_at ? new Date(session.completed_at).toLocaleString("pt-BR") : "",
          session.pdf_url ? "Sim" : "Não",
          ...answerColumns,
        ];
      });

      // Convert to CSV
      const escapeCSV = (value: string) => {
        if (value.includes(",") || value.includes('"') || value.includes("\n")) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };

      const csvContent = [
        headers.map(escapeCSV).join(","),
        ...rows.map((row) => row.map(escapeCSV).join(",")),
      ].join("\n");

      // Download file
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${formTitle.replace(/[^a-zA-Z0-9]/g, "_")}_respostas.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Exportação concluída",
        description: `${sessions.length} respostas exportadas com sucesso.`,
      });
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast({
        title: "Erro na exportação",
        description: "Não foi possível exportar os dados.",
        variant: "destructive",
      });
    }
  };

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-primary rounded-xl flex items-center justify-center shadow-glow">
              <Magnet className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-xl font-display font-bold">Magnet Link</span>
          </Link>
          <div className="flex items-center gap-4">
            {role === "admin" && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin">
                  <Shield className="mr-2 h-4 w-4" />
                  Admin
                </Link>
              </Button>
            )}
            <ProfileMenu />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 pb-[100px]">
        <div className="flex flex-col gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-display font-bold">Meus Formulários</h1>
            <p className="text-muted-foreground mt-1">
              Gerencie seus formulários de captação de leads
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Plan badge */}
            {role === "pro" && (
              <>
                <Badge className="bg-gradient-primary text-white">
                  <Crown className="mr-1 h-3 w-3" />
                  Pro
                </Badge>
                <ManageSubscriptionButton />
              </>
            )}
            {role === "admin" && (
              <Badge variant="secondary">
                Admin
              </Badge>
            )}
            {role === "free" && (
              <>
                <Badge variant="outline" className="text-muted-foreground">
                  {monthlyUsage}/50 respostas IA
                </Badge>
                <Button variant="outline" size="sm" asChild className="flex-shrink-0">
                  <Link to="/upgrade">
                    <Crown className="mr-2 h-4 w-4" />
                    Upgrade Pro
                  </Link>
                </Button>
              </>
            )}
            
            <Button variant="outline" size="sm" asChild className="flex-shrink-0">
              <Link to="/templates">
                <LayoutTemplate className="mr-2 h-4 w-4" />
                Templates
              </Link>
            </Button>
            
            {canCreateForm ? (
              <Button size="sm" asChild className="bg-gradient-primary hover:opacity-90 flex-shrink-0">
                <Link to="/form-builder">
                  <Plus className="mr-2 h-4 w-4" />
                  Novo Formulário
                </Link>
              </Button>
            ) : (
              <Button size="sm" asChild className="bg-gradient-primary hover:opacity-90 flex-shrink-0">
                <Link to="/upgrade">
                  <Crown className="mr-2 h-4 w-4" />
                  Fazer Upgrade
                </Link>
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2 mt-2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : forms.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-display font-semibold mb-2">
                Nenhum formulário ainda
              </h3>
              <p className="text-muted-foreground mb-6">
                Crie seu primeiro formulário inteligente para começar a capturar leads
              </p>
              <Button asChild className="bg-gradient-primary hover:opacity-90">
                <Link to="/form-builder">
                  <Plus className="mr-2 h-4 w-4" />
                  Criar primeiro formulário
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {forms.map((form) => (
              <Card key={form.id} className="group hover:shadow-lg transition-shadow overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <CardTitle className="text-lg font-display truncate">
                        {form.title}
                      </CardTitle>
                      <CardDescription className="mt-1 flex items-center gap-1">
                        <span className="truncate">/f/{form.slug}</span>
                        <a
                          href={`/f/${form.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary/80 transition-colors"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </CardDescription>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/form-builder/${form.id}`}>
                            <Edit className="mr-2 h-4 w-4" />
                            Editar
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={`/f/${form.slug}`} target="_blank" rel="noopener noreferrer">
                            <Eye className="mr-2 h-4 w-4" />
                            Visualizar
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(form.id)}>
                          <Copy className="mr-2 h-4 w-4" />
                          Duplicar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setFunnelForm({ id: form.id, title: form.title })}>
                          <TrendingDown className="mr-2 h-4 w-4" />
                          Ver Funil
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => {
                            if (canExportCsv) {
                              handleExportCSV(form.id, form.title);
                            } else {
                              navigate("/upgrade");
                            }
                          }}
                        >
                          <FileSpreadsheet className="mr-2 h-4 w-4" />
                          {canExportCsv ? "Exportar CSV" : "Upgrade para Exportar"}
                          {!canExportCsv && <Crown className="ml-2 h-3 w-3 text-primary" />}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setDeleteFormId(form.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="overflow-hidden">
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4 break-words">
                    {form.description || "Sem descrição"}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Users className="h-4 w-4 flex-shrink-0" />
                      <span>{form._count?.sessions || 0} leads</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <BarChart3 className="h-4 w-4 flex-shrink-0" />
                      <span>{form._count?.completed || 0} completos</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        form.is_active
                          ? "bg-success/10 text-success"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {form.is_active ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteFormId} onOpenChange={() => setDeleteFormId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir formulário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este formulário? Esta ação não pode ser desfeita
              e todos os leads capturados serão perdidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Funnel Dialog */}
      <FunnelDialog
        open={!!funnelForm}
        onOpenChange={(open) => !open && setFunnelForm(null)}
        formId={funnelForm?.id || ""}
        formTitle={funnelForm?.title || ""}
      />
    </div>
  );
}
